import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { moduleApi } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import {
    Users,
    Search,
    Phone,
    Mail,
    Building2,
    History,
    Calendar,
    FileText,
    MapPin,
    ChevronRight,
    ChevronDown,
    User,
    ArrowLeft,
    Briefcase,
    Trash2,
    Pencil,
    Save,
    CheckCircle,
    X,
    Plus,
    Loader2,
    Paperclip,
    CalendarClock,
    Truck,
    Package,
    BadgeCheck,
    Clock as ClockIcon,
    Wallet
} from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { Modal } from '../ui/Modal';
import { SendCustomMessageModal } from '../ui/SendCustomMessageModal';
import { SendDocumentModal } from './SendDocumentModal';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';

type TabType = 'PROFESSIONNEL' | 'PRIVÉ';

export const PRESTATION_PAYMENT_LABELS: Record<string, { label: string; color: string }> = {
    encaisse: { label: 'Payé', color: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' },
    a_encaisser: { label: 'À encaisser', color: 'bg-red-500/10 text-red-400 border-red-500/20' },
    a_regler_sous_x_jours: { label: 'À régler (délai)', color: 'bg-warning-500/10 text-warning-400 border-warning-500/20' },
};

export function PrestationMarkPaidAction({ appt, remainingDue, onUpdated }: { appt: any; remainingDue: number; onUpdated: () => void }) {
    const [open, setOpen] = useState(false);
    const [entries, setEntries] = useState<{ method: string; amount: number }[]>([{ method: '', amount: remainingDue }]);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    const updateEntry = (idx: number, patch: Partial<{ method: string; amount: number }>) =>
        setEntries((prev) => prev.map((e, i) => (i === idx ? { ...e, ...patch } : e)));
    const addEntry = () => {
        const allocated = entries.reduce((s, e) => s + Number(e.amount || 0), 0);
        setEntries((prev) => [...prev, { method: '', amount: Math.max(0, Math.round((remainingDue - allocated) * 100) / 100) }]);
    };
    const removeEntry = (idx: number) => setEntries((prev) => prev.filter((_, i) => i !== idx));
    const entriesSum = entries.reduce((s, e) => s + Number(e.amount || 0), 0);
    const entriesValid = entries.every((e) => e.method && Number(e.amount) > 0) && Math.abs(entriesSum - remainingDue) < 0.01;

    const handleConfirm = async () => {
        if (!entriesValid) return;
        setSaving(true);
        setError('');
        try {
            const res: any = await moduleApi.markPrestationAppointmentPaid(appt.id, entries);
            if (res?.error) { setError(res.error); setSaving(false); return; }
            if (res?.recipient_email && res?.invoice) {
                try {
                    const { generateInvoicePdfFromData } = await import('../Factures/invoicePdfGenerator');
                    const { pdf } = await generateInvoicePdfFromData({ invoice: res.invoice, payload: res.payload, snapshot: res.snapshot });
                    const dataUri: string = pdf.output('datauristring');
                    const pdfBase64 = dataUri.split(',')[1];
                    await moduleApi.sendPrestationInvoiceEmail(appt.id, { to: res.recipient_email, pdfBase64, filename: `${res.invoice?.invoice_number || 'facture'}.pdf` });
                } catch (pdfErr) {
                    console.error('Failed to generate/send updated invoice PDF', pdfErr);
                }
            }
            setOpen(false);
            onUpdated();
        } catch {
            setError('Erreur lors de la mise à jour du paiement.');
        } finally {
            setSaving(false);
        }
    };

    if (!open) {
        return (
            <button onClick={(e) => { e.stopPropagation(); setEntries([{ method: '', amount: remainingDue }]); setOpen(true); }}
                className="w-full flex items-center justify-center gap-2 px-4 py-2 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold transition-all">
                <CheckCircle size={13} /> Marquer comme payé ({new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(remainingDue)})
            </button>
        );
    }
    return (
        <div className="space-y-2 p-3 rounded-xl bg-red-500/5 border border-red-500/20">
            <p className="text-[10px] font-bold text-slate-500 dark:text-content-muted uppercase tracking-widest">Moyen(s) de paiement utilisé(s)</p>
            {entries.map((entry, idx) => (
                <div key={idx} className="space-y-1.5 p-2 rounded-xl bg-surface border border-gray-300 dark:border-border" onClick={(e) => e.stopPropagation()}>
                    <div className="flex gap-2">
                        {['Espèces', 'Virement', 'Carte bleue'].map((m) => (
                            <button key={m} onClick={(e) => { e.stopPropagation(); updateEntry(idx, { method: m }); }}
                                className={`flex-1 px-2 py-1.5 rounded-lg text-[11px] font-bold border transition-all ${entry.method === m ? 'bg-emerald-600 border-emerald-600 text-white' : 'border-gray-300 dark:border-border text-slate-500 dark:text-content-muted hover:text-slate-900 dark:hover:text-content'}`}>
                                {m}
                            </button>
                        ))}
                    </div>
                    {entries.length > 1 && (
                        <div className="flex items-center gap-2">
                            <input type="number" min={0} step="0.01" value={entry.amount}
                                onClick={(e) => e.stopPropagation()}
                                onChange={(e) => updateEntry(idx, { amount: Number(e.target.value || 0) })}
                                placeholder="Montant" title="Montant pour ce moyen de paiement"
                                className="flex-1 bg-surface-soft border border-gray-300 dark:border-border rounded-lg px-2 py-1.5 text-sm text-slate-900 dark:text-content outline-none focus:border-brand-500" />
                            <button type="button" onClick={(e) => { e.stopPropagation(); removeEntry(idx); }} className="text-red-400 p-1 shrink-0"><Trash2 size={14} /></button>
                        </div>
                    )}
                </div>
            ))}
            <button type="button" onClick={(e) => { e.stopPropagation(); addEntry(); }} className="text-xs font-bold text-brand-600 dark:text-brand-400 flex items-center gap-1.5">
                <Plus size={14} /> Ajouter un autre moyen de paiement
            </button>
            {entries.length > 1 && Math.abs(entriesSum - remainingDue) >= 0.01 && (
                <p className="text-xs text-red-400 font-semibold">
                    La somme des moyens de paiement ({new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(entriesSum)}) ne correspond pas au reste à payer ({new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(remainingDue)}).
                </p>
            )}
            {error && <p className="text-xs text-red-400 font-semibold">{error}</p>}
            <div className="flex gap-2">
                <button onClick={(e) => { e.stopPropagation(); setOpen(false); }} className="flex-1 px-3 py-1.5 rounded-lg border border-gray-300 dark:border-border text-slate-500 dark:text-content-muted text-xs font-bold">Annuler</button>
                <button onClick={(e) => { e.stopPropagation(); handleConfirm(); }} disabled={!entriesValid || saving} className="flex-1 px-3 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold disabled:opacity-50">
                    {saving ? 'Validation...' : 'Confirmer'}
                </button>
            </div>
        </div>
    );
}

export const CRMModule = () => {
    const { user } = useAuth();
    const navigate = useNavigate();
    // Route optionnelle /crm/contacts/:id — permet aux pages d'historique dédiées (factures, commandes,
    // rendez-vous/événements) de naviguer explicitement vers la fiche d'un contact précis à leur retour,
    // plutôt que navigate(-1) qui pouvait retomber sur la liste (selectedContact n'est qu'un état local,
    // perdu si la page était remontée depuis une autre route).
    const { id: routeContactId } = useParams<{ id?: string }>();
    const isPrestationClient = (user as any)?.client_type === 'prestation';
    const [contacts, setContacts] = useState<any[]>([]);
    const [selectedContact, setSelectedContact] = useState<any | null>(null);
    const [showSendMessage, setShowSendMessage] = useState(false);
    const [showSendDocument, setShowSendDocument] = useState(false);
    const [sendingRdvLink, setSendingRdvLink] = useState(false);
    const [rdvLinkSent, setRdvLinkSent] = useState(false);
    const [documentRequests, setDocumentRequests] = useState<any[]>([]);
    const [loadingDocumentRequests, setLoadingDocumentRequests] = useState(false);
    const [contactOrders, setContactOrders] = useState<any[]>([]);
    const [loadingContactOrders, setLoadingContactOrders] = useState(false);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState<TabType>('PROFESSIONNEL');
    const [searchPro, setSearchPro] = useState('');
    const [searchPrive, setSearchPrive] = useState('');
    const [deletingContactId, setDeletingContactId] = useState<string | null>(null);
    const [actionError, setActionError] = useState('');
    const [isEditingContact, setIsEditingContact] = useState(false);
    const [savingContact, setSavingContact] = useState(false);
    const [editForm, setEditForm] = useState({
        type: 'PROFESSIONNEL' as TabType,
        first_name: '',
        last_name: '',
        company_name: '',
        organizer_name: '',
        phone: '',
        email: '',
        address: '',
        postal_code: '',
        city: '',
        country: 'France'
    });
    const [showAddModal, setShowAddModal] = useState(false);
    const [creatingContact, setCreatingContact] = useState(false);
    const [addError, setAddError] = useState('');
    const [addForm, setAddForm] = useState({
        type: 'PROFESSIONNEL' as TabType,
        first_name: '',
        last_name: '',
        company_name: '',
        organizer_name: '',
        phone: '',
        email: '',
        address: '',
        postal_code: '',
        city: '',
        country: 'France'
    });

    useEffect(() => {
        loadContacts();
    }, []);

    const loadDocumentRequests = (contactId: string) => {
        setLoadingDocumentRequests(true);
        moduleApi.getContactDocumentRequests(contactId)
            .then((d: any) => setDocumentRequests(Array.isArray(d) ? d : []))
            .catch(() => setDocumentRequests([]))
            .finally(() => setLoadingDocumentRequests(false));
    };

    const loadContactOrders = (contactId: string) => {
        setLoadingContactOrders(true);
        moduleApi.getContactOrders(contactId)
            .then((d: any) => setContactOrders(Array.isArray(d) ? d : []))
            .catch(() => setContactOrders([]))
            .finally(() => setLoadingContactOrders(false));
    };

    useEffect(() => {
        if (selectedContact?.id) { loadDocumentRequests(selectedContact.id); loadContactOrders(selectedContact.id); }
        else { setDocumentRequests([]); setContactOrders([]); }
        setRdvLinkSent(false);
    }, [selectedContact?.id]);

    const handleSendRdvLink = async () => {
        if (!selectedContact) return;
        setSendingRdvLink(true);
        try {
            const res: any = await moduleApi.sendBookingLinkEmail({ crm_contact_id: selectedContact.id });
            if (!res?.error) setRdvLinkSent(true);
        } catch (e) {
            console.error('Failed to send booking link', e);
        } finally {
            setSendingRdvLink(false);
        }
    };

    const loadContacts = async () => {
        setLoading(true);
        try {
            const data = await moduleApi.getCRMContacts();
            setContacts(data || []);
        } catch (e) {
            console.error(e);
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteContact = async (contact: any) => {
        const contactLabel = contact.type === 'PRIVÉ'
            ? `${contact.first_name ?? ''} ${contact.last_name ?? ''}`.trim() || 'ce contact'
            : contact.company_name || contact.organizer_name || 'ce contact';

        if (!window.confirm(`Supprimer ${contactLabel} du CRM ?`)) {
            return;
        }

        setActionError('');
        setDeletingContactId(contact.id);

        try {
            await moduleApi.deleteCRMContact(contact.id);
            if (selectedContact?.id === contact.id) {
                setSelectedContact(null);
            }
            await loadContacts();
        } catch (err: any) {
            setActionError(err.message || 'Impossible de supprimer ce contact.');
        } finally {
            setDeletingContactId(null);
        }
    };

    const loadContactDetail = async (id: string) => {
        try {
            const data = await moduleApi.getCRMContact(id);
            setSelectedContact(data);
            setIsEditingContact(false);
        } catch (e) {
            console.error(e);
        }
    };

    // Ouvre directement la fiche du contact désigné par l'URL (/crm/contacts/:id) — utilisé quand on
    // revient depuis une page d'historique dédiée (factures, commandes, démarches, rendez-vous/événements)
    // pour retomber exactement sur cette fiche plutôt que sur la liste.
    useEffect(() => {
        if (routeContactId && selectedContact?.id !== routeContactId) loadContactDetail(routeContactId);
    }, [routeContactId]);

    const startEditContact = (contact: any) => {
        setActionError('');
        setEditForm({
            type: contact?.type === 'PRIVÉ' ? 'PRIVÉ' : 'PROFESSIONNEL',
            first_name: contact?.first_name || '',
            last_name: contact?.last_name || '',
            company_name: contact?.company_name || '',
            organizer_name: contact?.organizer_name || '',
            phone: contact?.phone || '',
            email: contact?.email || '',
            address: contact?.address || '',
            postal_code: contact?.postal_code || '',
            city: contact?.city || '',
            country: contact?.country || 'France'
        });
        setIsEditingContact(true);
    };

    const saveEditedContact = async () => {
        if (!selectedContact) return;
        setActionError('');
        setSavingContact(true);
        try {
            await moduleApi.updateCRMContact(selectedContact.id, editForm);
            const refreshed = await moduleApi.getCRMContact(selectedContact.id);
            setSelectedContact(refreshed);
            await loadContacts();
            setIsEditingContact(false);
        } catch (err: any) {
            setActionError(err.message || 'Impossible de modifier ce contact.');
        } finally {
            setSavingContact(false);
        }
    };

    const openAddModal = () => {
        setAddError('');
        setAddForm({
            type: activeTab,
            first_name: '',
            last_name: '',
            company_name: '',
            organizer_name: '',
            phone: '',
            email: '',
            address: '',
            postal_code: '',
            city: '',
            country: 'France'
        });
        setShowAddModal(true);
    };

    const handleCreateContact = async () => {
        setAddError('');
        if (addForm.type === 'PRIVÉ' && !addForm.last_name.trim() && !addForm.first_name.trim()) {
            setAddError('Merci de renseigner au moins le nom ou le prénom.');
            return;
        }
        if (addForm.type === 'PROFESSIONNEL' && !addForm.company_name.trim()) {
            setAddError('Merci de renseigner le nom de l\'entreprise.');
            return;
        }
        setCreatingContact(true);
        try {
            await moduleApi.createCRMContact(addForm);
            setShowAddModal(false);
            setActiveTab(addForm.type);
            await loadContacts();
        } catch (err: any) {
            setAddError(err.message || 'Impossible de créer ce contact.');
        } finally {
            setCreatingContact(false);
        }
    };

    const search = activeTab === 'PROFESSIONNEL' ? searchPro : searchPrive;
    const setSearch = activeTab === 'PROFESSIONNEL' ? setSearchPro : setSearchPrive;

    const proContacts = contacts.filter(c => c.type === 'PROFESSIONNEL');
    const priveContacts = contacts.filter(c => c.type === 'PRIVÉ');

    const filteredContacts = (activeTab === 'PROFESSIONNEL' ? proContacts : priveContacts).filter(c =>
        `${c.first_name ?? ''} ${c.last_name ?? ''} ${c.company_name ?? ''} ${c.phone ?? ''} ${c.address ?? ''} ${c.postal_code ?? ''} ${c.city ?? ''}`
            .toLowerCase()
            .includes(search.toLowerCase())
    ).sort((a, b) => {
        if (activeTab === 'PROFESSIONNEL') {
            const nameA = (a.company_name || a.organizer_name || '').toUpperCase();
            const nameB = (b.company_name || b.organizer_name || '').toUpperCase();
            return nameA.localeCompare(nameB, 'fr');
        } else {
            const nameA = `${a.last_name || ''} ${a.first_name || ''}`.trim().toUpperCase();
            const nameB = `${b.last_name || ''} ${b.first_name || ''}`.trim().toUpperCase();
            return nameA.localeCompare(nameB, 'fr');
        }
    });

    if (selectedContact) {
        return (
            <div className="space-y-6">
                <header className="flex items-center justify-between gap-4 flex-wrap">
                    <div className="flex items-center gap-3">
                        <button
                            onClick={() => { setSelectedContact(null); if (routeContactId) navigate('/crm'); }}
                            className="p-2 hover:bg-surface-soft rounded-lg text-content-muted"
                        >
                            <ArrowLeft size={18} />
                        </button>
                        <div>
                        <h1 className="text-xl font-bold text-content tracking-tight">Fiche Contact</h1>
                        <p className="text-content-muted text-sm">{isPrestationClient ? 'Détails et historique des rendez-vous' : 'Détails et historique des privatisations'}</p>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        {!isEditingContact && selectedContact.email && (
                            <>
                                <button
                                    onClick={() => setShowSendDocument(true)}
                                    className="inline-flex items-center gap-2 rounded-xl border border-border px-4 py-2 text-sm font-semibold text-content-muted hover:text-content hover:bg-surface-soft transition-all"
                                >
                                    <FileText size={16} />
                                    Envoyer une démarche
                                </button>
                                {isPrestationClient && (
                                    <button
                                        onClick={handleSendRdvLink}
                                        disabled={sendingRdvLink}
                                        className="inline-flex items-center gap-2 rounded-xl border border-border px-4 py-2 text-sm font-semibold text-content-muted hover:text-content hover:bg-surface-soft transition-all disabled:opacity-50"
                                    >
                                        <CalendarClock size={16} />
                                        {sendingRdvLink ? 'Envoi...' : rdvLinkSent ? 'Lien envoyé ✓' : 'Envoyer un lien de rendez-vous'}
                                    </button>
                                )}
                                <button
                                    onClick={() => setShowSendMessage(true)}
                                    className="inline-flex items-center gap-2 rounded-xl border border-border px-4 py-2 text-sm font-semibold text-content-muted hover:text-content hover:bg-surface-soft transition-all"
                                >
                                    <Mail size={16} />
                                    Message personnalisé
                                </button>
                            </>
                        )}
                        {!isEditingContact && (
                            <button
                                onClick={() => startEditContact(selectedContact)}
                                className="inline-flex items-center gap-2 rounded-xl border border-blue-500/20 bg-blue-500/10 px-4 py-2 text-sm font-semibold text-blue-300 transition-all hover:bg-blue-500/20"
                            >
                                <Pencil size={16} />
                                Modifier
                            </button>
                        )}
                        <button
                            onClick={() => handleDeleteContact(selectedContact)}
                            disabled={deletingContactId === selectedContact.id || savingContact}
                            className="inline-flex items-center gap-2 rounded-xl border border-red-500/20 bg-red-500/10 px-4 py-2 text-sm font-semibold text-red-300 transition-all hover:bg-red-500/20 disabled:cursor-not-allowed disabled:opacity-60"
                        >
                            <Trash2 size={16} />
                            {deletingContactId === selectedContact.id ? 'Suppression...' : 'Supprimer le contact'}
                        </button>
                    </div>
                </header>

                {actionError && (
                    <div className="rounded-2xl border border-red-500/20 bg-red-500/10 px-4 py-3 text-sm text-red-300">
                        {actionError}
                    </div>
                )}

                {isEditingContact && (
                    <div className="rounded-2xl border border-border bg-white dark:bg-surface p-5 space-y-4 transition-colors duration-200">
                        <h3 className="text-sm font-bold text-slate-900 dark:text-content uppercase tracking-widest">Modifier le contact</h3>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <div className="space-y-1">
                                <label className="text-[10px] font-bold text-slate-600 dark:text-content-muted uppercase tracking-widest">Type</label>
                                <select
                                    value={editForm.type}
                                    onChange={(e) => setEditForm((prev) => ({ ...prev, type: e.target.value as TabType }))}
                                    className="w-full bg-surface border border-gray-300 dark:border-border rounded-lg px-3 py-2 text-sm text-slate-900 dark:text-content outline-none"
                                >
                                    <option value="PROFESSIONNEL">Professionnel</option>
                                    <option value="PRIVÉ">Privé</option>
                                </select>
                            </div>
                            <div className="space-y-1">
                                <label className="text-[10px] font-bold text-slate-600 dark:text-content-muted uppercase tracking-widest">Téléphone</label>
                                <input
                                    type="text"
                                    value={editForm.phone}
                                    onChange={(e) => setEditForm((prev) => ({ ...prev, phone: e.target.value }))}
                                    className="w-full bg-surface border border-gray-300 dark:border-border rounded-lg px-3 py-2 text-sm text-slate-900 dark:text-content outline-none"
                                />
                            </div>

                            <div className="space-y-1">
                                <label className="text-[10px] font-bold text-slate-600 dark:text-content-muted uppercase tracking-widest">Prénom</label>
                                <input
                                    type="text"
                                    value={editForm.first_name}
                                    onChange={(e) => setEditForm((prev) => ({ ...prev, first_name: e.target.value }))}
                                    className="w-full bg-surface border border-gray-300 dark:border-border rounded-lg px-3 py-2 text-sm text-slate-900 dark:text-content outline-none"
                                />
                            </div>
                            <div className="space-y-1">
                                <label className="text-[10px] font-bold text-slate-600 dark:text-content-muted uppercase tracking-widest">Nom</label>
                                <input
                                    type="text"
                                    value={editForm.last_name}
                                    onChange={(e) => setEditForm((prev) => ({ ...prev, last_name: e.target.value }))}
                                    className="w-full bg-surface border border-gray-300 dark:border-border rounded-lg px-3 py-2 text-sm text-slate-900 dark:text-content outline-none"
                                />
                            </div>

                            <div className="space-y-1">
                                <label className="text-[10px] font-bold text-slate-600 dark:text-content-muted uppercase tracking-widest">Entreprise</label>
                                <input
                                    type="text"
                                    value={editForm.company_name}
                                    onChange={(e) => setEditForm((prev) => ({ ...prev, company_name: e.target.value }))}
                                    className="w-full bg-surface border border-gray-300 dark:border-border rounded-lg px-3 py-2 text-sm text-slate-900 dark:text-content outline-none"
                                />
                            </div>
                            <div className="space-y-1">
                                <label className="text-[10px] font-bold text-slate-600 dark:text-content-muted uppercase tracking-widest">Organisateur</label>
                                <input
                                    type="text"
                                    value={editForm.organizer_name}
                                    onChange={(e) => setEditForm((prev) => ({ ...prev, organizer_name: e.target.value }))}
                                    className="w-full bg-surface border border-gray-300 dark:border-border rounded-lg px-3 py-2 text-sm text-slate-900 dark:text-content outline-none"
                                />
                            </div>

                            <div className="md:col-span-2 space-y-1">
                                <label className="text-[10px] font-bold text-slate-600 dark:text-content-muted uppercase tracking-widest">Email</label>
                                <input
                                    type="email"
                                    value={editForm.email}
                                    onChange={(e) => setEditForm((prev) => ({ ...prev, email: e.target.value }))}
                                    className="w-full bg-surface border border-gray-300 dark:border-border rounded-lg px-3 py-2 text-sm text-slate-900 dark:text-content outline-none"
                                />
                            </div>

                            <div className="md:col-span-2 space-y-1">
                                <label className="text-[10px] font-bold text-slate-600 dark:text-content-muted uppercase tracking-widest">Adresse postale</label>
                                <input
                                    type="text"
                                    value={editForm.address}
                                    onChange={(e) => setEditForm((prev) => ({ ...prev, address: e.target.value }))}
                                    className="w-full bg-surface border border-gray-300 dark:border-border rounded-lg px-3 py-2 text-sm text-slate-900 dark:text-content outline-none"
                                />
                            </div>

                            <div className="space-y-1">
                                <label className="text-[10px] font-bold text-slate-600 dark:text-content-muted uppercase tracking-widest">Code postal</label>
                                <input
                                    type="text"
                                    value={editForm.postal_code}
                                    onChange={(e) => setEditForm((prev) => ({ ...prev, postal_code: e.target.value }))}
                                    className="w-full bg-surface border border-gray-300 dark:border-border rounded-lg px-3 py-2 text-sm text-slate-900 dark:text-content outline-none"
                                />
                            </div>

                            <div className="space-y-1">
                                <label className="text-[10px] font-bold text-slate-600 dark:text-content-muted uppercase tracking-widest">Ville</label>
                                <input
                                    type="text"
                                    value={editForm.city}
                                    onChange={(e) => setEditForm((prev) => ({ ...prev, city: e.target.value }))}
                                    className="w-full bg-surface border border-gray-300 dark:border-border rounded-lg px-3 py-2 text-sm text-slate-900 dark:text-content outline-none"
                                />
                            </div>

                            <div className="md:col-span-2 space-y-1">
                                <label className="text-[10px] font-bold text-slate-600 dark:text-content-muted uppercase tracking-widest">Pays</label>
                                <input
                                    type="text"
                                    value={editForm.country}
                                    onChange={(e) => setEditForm((prev) => ({ ...prev, country: e.target.value }))}
                                    className="w-full bg-surface border border-gray-300 dark:border-border rounded-lg px-3 py-2 text-sm text-slate-900 dark:text-content outline-none"
                                />
                            </div>
                        </div>

                        <div className="flex items-center justify-end gap-2 pt-1">
                            <button
                                type="button"
                                onClick={() => setIsEditingContact(false)}
                                disabled={savingContact}
                                className="inline-flex items-center gap-2 rounded-lg border border-gray-300 dark:border-border px-3 py-2 text-xs font-bold text-slate-700 dark:text-content-muted hover:bg-slate-100 dark:hover:bg-surface-soft disabled:opacity-60"
                            >
                                <X size={14} /> Annuler
                            </button>
                            <button
                                type="button"
                                onClick={saveEditedContact}
                                disabled={savingContact}
                                className="inline-flex items-center gap-2 rounded-lg bg-white px-3 py-2 text-xs font-bold text-content hover:bg-gray-200 disabled:opacity-60"
                            >
                                <Save size={14} /> {savingContact ? 'Enregistrement...' : 'Enregistrer'}
                            </button>
                        </div>
                    </div>
                )}

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
                    {/* Profil Card */}
                    <div className="lg:col-span-1 space-y-6">
                        <div className="bg-white dark:bg-surface rounded-3xl border border-border p-8 text-center transition-colors duration-200">
                            <div className="w-24 h-24 bg-surface-soft dark:bg-surface-soft rounded-full flex items-center justify-center mx-auto mb-6 border border-border">
                                <User size={48} className="text-slate-700 dark:text-content" />
                            </div>
                            <h2 className="text-2xl font-bold text-slate-900 dark:text-content">
                                {selectedContact.type === 'PRIVÉ' ? `${(selectedContact.last_name || '').toUpperCase()} ${selectedContact.first_name || ''}`.trim() : selectedContact.company_name}
                            </h2>
                            <p className="text-blue-400 text-xs font-bold uppercase tracking-widest mt-2">{selectedContact.type}</p>
                            
                            <div className="mt-10 space-y-4 text-left">
                                <div className="flex items-center gap-4 p-4 bg-surface rounded-2xl border border-border transition-colors duration-200">
                                    <Phone size={18} className="text-slate-500 dark:text-content-muted" />
                                    <span className="text-slate-900 dark:text-content font-medium">{selectedContact.phone}</span>
                                </div>
                                <div className="flex items-center gap-4 p-4 bg-surface rounded-2xl border border-border transition-colors duration-200">
                                    <Mail size={18} className="text-slate-500 dark:text-content-muted" />
                                    <span className="text-slate-900 dark:text-content font-medium">{selectedContact.email || 'Non renseigné'}</span>
                                </div>
                                {selectedContact.type === 'PROFESSIONNEL' && (
                                    <div className="flex items-center gap-4 p-4 bg-surface rounded-2xl border border-border transition-colors duration-200">
                                        <Building2 size={18} className="text-slate-500 dark:text-content-muted" />
                                        <span className="text-slate-900 dark:text-content font-medium">{selectedContact.organizer_name}</span>
                                    </div>
                                )}
                                {(selectedContact.address || selectedContact.postal_code || selectedContact.city) && (
                                    <div className="flex items-start gap-4 p-4 bg-surface rounded-2xl border border-border transition-colors duration-200">
                                        <MapPin size={18} className="text-slate-500 dark:text-content-muted mt-0.5" />
                                        <div className="text-slate-900 dark:text-content font-medium text-sm">
                                            <div>{selectedContact.address || 'Adresse non renseignée'}</div>
                                            <div>{[selectedContact.postal_code, selectedContact.city, selectedContact.country].filter(Boolean).join(' ')}</div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Historique — chaque catégorie ouvre sa propre page dédiée (recherche + tri), pour ne
                        pas surcharger la fiche contact d'une longue liste ; le bouton retour de ces pages
                        ramène ici (cf. navigate(-1) dans chaque page). */}
                    <div className="lg:col-span-2 space-y-3">
                        <button
                            onClick={() => navigate(`/crm/contacts/${selectedContact.id}/factures`)}
                            className="w-full flex items-center justify-between gap-4 bg-white dark:bg-surface p-4 rounded-2xl border border-border hover:border-brand-500/40 transition-colors text-left"
                        >
                            <div className="flex items-center gap-3">
                                <FileText className="text-slate-900 dark:text-content" size={22} />
                                <div>
                                    <p className="font-bold text-slate-900 dark:text-content">Factures liées</p>
                                    <p className="text-xs text-slate-500 dark:text-content-muted mt-0.5">
                                        {selectedContact?.factures?.length || 0} facture{(selectedContact?.factures?.length || 0) > 1 ? 's' : ''}
                                    </p>
                                </div>
                            </div>
                            <ChevronRight className="text-slate-500 dark:text-content-muted shrink-0" size={20} />
                        </button>

                        <button
                            onClick={() => navigate(`/crm/contacts/${selectedContact.id}/demarches`)}
                            className="w-full flex items-center justify-between gap-4 bg-white dark:bg-surface p-4 rounded-2xl border border-border hover:border-brand-500/40 transition-colors text-left"
                        >
                            <div className="flex items-center gap-3">
                                <FileText className="text-slate-900 dark:text-content" size={22} />
                                <div>
                                    <p className="font-bold text-slate-900 dark:text-content">Historique des démarches</p>
                                    <p className="text-xs text-slate-500 dark:text-content-muted mt-0.5">
                                        {loadingDocumentRequests ? 'Chargement...' : `${documentRequests.length} démarche${documentRequests.length > 1 ? 's' : ''} envoyée${documentRequests.length > 1 ? 's' : ''}`}
                                    </p>
                                </div>
                            </div>
                            <ChevronRight className="text-slate-500 dark:text-content-muted shrink-0" size={20} />
                        </button>

                        <button
                            onClick={() => navigate(`/crm/contacts/${selectedContact.id}/commandes`)}
                            className="w-full flex items-center justify-between gap-4 bg-white dark:bg-surface p-4 rounded-2xl border border-border hover:border-brand-500/40 transition-colors text-left"
                        >
                            <div className="flex items-center gap-3">
                                <Truck className="text-slate-900 dark:text-content" size={22} />
                                <div>
                                    <p className="font-bold text-slate-900 dark:text-content">Historique des commandes</p>
                                    <p className="text-xs text-slate-500 dark:text-content-muted mt-0.5">
                                        {loadingContactOrders ? 'Chargement...' : `${contactOrders.length} commande${contactOrders.length > 1 ? 's' : ''}`}
                                    </p>
                                </div>
                            </div>
                            <ChevronRight className="text-slate-500 dark:text-content-muted shrink-0" size={20} />
                        </button>

                        <button
                            onClick={() => navigate(`/crm/contacts/${selectedContact.id}/${isPrestationClient ? 'rendez-vous' : 'evenements'}`)}
                            className="w-full flex items-center justify-between gap-4 bg-white dark:bg-surface p-4 rounded-2xl border border-border hover:border-brand-500/40 transition-colors text-left"
                        >
                            <div className="flex items-center gap-3">
                                <History className="text-slate-900 dark:text-content" size={22} />
                                <div>
                                    <p className="font-bold text-slate-900 dark:text-content">{isPrestationClient ? 'Historique des rendez-vous' : 'Historique des Événements'}</p>
                                    <p className="text-xs text-slate-500 dark:text-content-muted mt-0.5">
                                        {isPrestationClient
                                            ? `${(selectedContact.appointments || []).length} rendez-vous`
                                            : `${(selectedContact.events || []).length} événement${(selectedContact.events || []).length > 1 ? 's' : ''}`}
                                    </p>
                                </div>
                            </div>
                            <ChevronRight className="text-slate-500 dark:text-content-muted shrink-0" size={20} />
                        </button>
                    </div>
                </div>
                {showSendMessage && (
                    <SendCustomMessageModal
                        recipientLabel={selectedContact.type === 'PRIVÉ' ? `${selectedContact.first_name || ''} ${selectedContact.last_name || ''}`.trim() : selectedContact.company_name}
                        onClose={() => setShowSendMessage(false)}
                        onSend={(subject, body) => moduleApi.sendCRMContactMessage(selectedContact.id, { subject, body })}
                    />
                )}
                {showSendDocument && (
                    <SendDocumentModal
                        contactId={selectedContact.id}
                        onClose={() => setShowSendDocument(false)}
                        onSent={() => loadDocumentRequests(selectedContact.id)}
                    />
                )}
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {actionError && (
                <div className="rounded-xl border border-red-500/20 bg-red-500/10 px-4 py-3 text-sm text-red-300">
                    {actionError}
                </div>
            )}

            {/* Header */}
            <header className="flex items-center justify-between flex-wrap gap-3">
                <div>
                    <h1 className="text-xl font-bold text-content tracking-tight">CRM Contacts</h1>
                    <p className="text-content-muted text-sm mt-0.5">{isPrestationClient ? 'Base de données clients et historique des rendez-vous' : 'Base de données clients et historique événementiel'}</p>
                </div>
                <div className="flex items-center gap-2">
                    <div className="relative w-72">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-content-muted" size={15} />
                        <Input
                            type="text"
                            placeholder={activeTab === 'PROFESSIONNEL' ? 'Rechercher un professionnel...' : 'Rechercher un client privé...'}
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                            className="pl-8"
                        />
                    </div>
                    <Button onClick={openAddModal} className="shrink-0">
                        <Plus size={15} />
                        Ajouter un contact
                    </Button>
                </div>
            </header>

            {/* Tabs */}
            <div className="flex gap-1 bg-surface p-1 rounded-xl border border-border w-fit transition-colors duration-200">
                {([
                    { key: 'PROFESSIONNEL', label: 'Clients Professionnels', icon: Briefcase, count: proContacts.length },
                    { key: 'PRIVÉ',         label: 'Clients Privés',         icon: User,     count: priveContacts.length },
                ] as { key: TabType; label: string; icon: any; count: number }[]).map(tab => (
                    <button
                        key={tab.key}
                        onClick={() => setActiveTab(tab.key)}
                        className={`flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold transition-all ${
                            activeTab === tab.key
                                ? 'bg-brand-600 text-brand-fg shadow-sm'
                                : 'text-[var(--text-muted)] hover:bg-brand-600/10 hover:text-brand-600 dark:hover:text-brand-400'
                        }`}
                    >
                        <tab.icon size={15} />
                        {tab.label}
                        <span className={`ml-1 text-xs font-bold px-2 py-0.5 rounded-full ${
                            activeTab === tab.key ? 'bg-white/20 text-brand-fg' : 'bg-surface-soft text-[var(--text-muted)]'
                        }`}>{tab.count}</span>
                    </button>
                ))}
            </div>

            {/* Grid */}
            <AnimatePresence mode="wait">
                <motion.div
                    key={activeTab}
                    initial={{ opacity: 0, y: 6 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -6 }}
                    transition={{ duration: 0.18 }}
                    className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
                >
                    {filteredContacts.map((contact, i) => (
                        <motion.button
                            key={contact.id}
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: i * 0.04 }}
                            onClick={() => loadContactDetail(contact.id)}
                            className="bg-white dark:bg-surface p-6 rounded-2xl border border-border hover:border-gray-300 dark:hover:border-white/20 transition-all text-left group"
                        >
                            {activeTab === 'PROFESSIONNEL' ? (
                                /* ── Carte Professionnel ── */
                                <>
                                    <div className="flex items-center gap-4 mb-5">
                                        <div className="w-12 h-12 bg-surface-soft dark:bg-surface-soft rounded-xl flex items-center justify-center border border-border group-hover:bg-surface dark:group-hover:bg-white group-hover:text-content dark:group-hover:text-slate-900 transition-all shrink-0">
                                            <Building2 size={22} />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <h3 className="text-lg font-bold text-slate-900 dark:text-content truncate">
                                                {contact.company_name || '—'}
                                            </h3>
                                            {contact.organizer_name && (
                                                <p className="text-xs text-slate-500 dark:text-content-muted truncate">{contact.organizer_name}</p>
                                            )}
                                        </div>
                                        <ChevronRight className="text-slate-400 dark:text-gray-700 group-hover:text-slate-900 dark:group-hover:text-slate-900 transition-all shrink-0" size={20} />
                                    </div>
                                    <div className="space-y-3">
                                        <div className="flex items-center gap-3 text-sm text-slate-600 dark:text-content-muted">
                                            <Phone size={14} className="text-slate-500 dark:text-gray-600 shrink-0" />
                                            <span>{contact.phone}</span>
                                        </div>
                                        <div className="flex items-center gap-3 text-sm text-slate-600 dark:text-content-muted">
                                            <Mail size={14} className="text-slate-500 dark:text-gray-600 shrink-0" />
                                            <span className="truncate">{contact.email || 'N/A'}</span>
                                        </div>
                                    </div>
                                </>
                            ) : (
                                /* ── Carte Privé ── */
                                <>
                                    <div className="flex items-center gap-4 mb-5">
                                        <div className="w-12 h-12 bg-surface-soft dark:bg-surface-soft rounded-full flex items-center justify-center border border-border group-hover:bg-surface dark:group-hover:bg-white group-hover:text-content dark:group-hover:text-slate-900 transition-all shrink-0">
                                            <User size={22} />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <h3 className="text-lg font-bold text-slate-900 dark:text-content truncate">
                                                {`${(contact.last_name || '').toUpperCase()} ${contact.first_name || ''}`.trim() || '—'}
                                            </h3>
                                            <p className="text-xs text-slate-500 dark:text-content-muted uppercase tracking-widest font-bold">Particulier</p>
                                        </div>
                                        <ChevronRight className="text-slate-400 dark:text-gray-700 group-hover:text-slate-900 dark:group-hover:text-slate-900 transition-all shrink-0" size={20} />
                                    </div>
                                    <div className="space-y-3">
                                        <div className="flex items-center gap-3 text-sm text-slate-600 dark:text-content-muted">
                                            <Phone size={14} className="text-slate-500 dark:text-gray-600 shrink-0" />
                                            <span>{contact.phone}</span>
                                        </div>
                                        <div className="flex items-center gap-3 text-sm text-slate-600 dark:text-content-muted">
                                            <Mail size={14} className="text-slate-500 dark:text-gray-600 shrink-0" />
                                            <span className="truncate">{contact.email || 'N/A'}</span>
                                        </div>
                                    </div>
                                </>
                            )}

                            <div className="mt-5 pt-5 border-t border-border flex items-center justify-between">
                                <span className="text-[10px] text-slate-500 dark:text-gray-600 font-bold uppercase tracking-widest">Dernière activité</span>
                                <span className="text-[10px] text-slate-600 dark:text-content-muted font-bold">{new Date(contact.updated_at).toLocaleDateString()}</span>
                            </div>
                        </motion.button>
                    ))}

                    {filteredContacts.length === 0 && !loading && (
                        <div className="col-span-full py-20 text-center bg-white dark:bg-surface rounded-3xl border border-dashed border-gray-300 dark:border-border transition-colors duration-200">
                            <Users size={48} className="text-slate-400 dark:text-gray-700 mx-auto mb-4" />
                            <p className="text-slate-500 dark:text-content-muted font-medium">
                                {search ? 'Aucun résultat pour cette recherche' : 'Aucun contact dans cette catégorie'}
                            </p>
                        </div>
                    )}
                </motion.div>
            </AnimatePresence>

            <AnimatePresence>
                {showAddModal && (
                    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[60] flex items-center justify-center p-4 overflow-y-auto">
                        <motion.div
                            initial={{ opacity: 0, scale: 0.95, y: 20 }}
                            animate={{ opacity: 1, scale: 1, y: 0 }}
                            exit={{ opacity: 0, scale: 0.95, y: 20 }}
                            className="bg-white dark:bg-surface border border-border rounded-2xl w-full max-w-xl shadow-2xl overflow-hidden my-8"
                        >
                            <div className="px-6 py-4 border-b border-border flex items-center justify-between">
                                <h3 className="text-lg font-bold text-slate-900 dark:text-content">Ajouter un contact</h3>
                                <button onClick={() => setShowAddModal(false)} className="text-content-muted hover:text-gray-700 dark:hover:text-slate-900 transition-colors">
                                    <X size={20} />
                                </button>
                            </div>
                            <div className="p-6 space-y-4 max-h-[75vh] overflow-y-auto">
                                <div className="flex gap-1 bg-surface-soft p-1 rounded-xl border border-border w-fit">
                                    {(['PROFESSIONNEL', 'PRIVÉ'] as TabType[]).map((t) => (
                                        <button
                                            key={t}
                                            type="button"
                                            onClick={() => setAddForm((prev) => ({ ...prev, type: t }))}
                                            className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all ${addForm.type === t ? 'bg-brand-600 text-brand-fg shadow-sm' : 'text-content-muted hover:text-content'}`}
                                        >
                                            {t === 'PROFESSIONNEL' ? 'Professionnel' : 'Privé'}
                                        </button>
                                    ))}
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                    {addForm.type === 'PRIVÉ' ? (
                                        <>
                                            <div className="space-y-1">
                                                <label className="text-[10px] font-bold text-slate-600 dark:text-content-muted uppercase tracking-widest">Prénom</label>
                                                <input
                                                    type="text"
                                                    value={addForm.first_name}
                                                    onChange={(e) => setAddForm((prev) => ({ ...prev, first_name: e.target.value }))}
                                                    className="w-full bg-surface border border-gray-300 dark:border-border rounded-lg px-3 py-2 text-sm text-slate-900 dark:text-content outline-none"
                                                />
                                            </div>
                                            <div className="space-y-1">
                                                <label className="text-[10px] font-bold text-slate-600 dark:text-content-muted uppercase tracking-widest">Nom</label>
                                                <input
                                                    type="text"
                                                    value={addForm.last_name}
                                                    onChange={(e) => setAddForm((prev) => ({ ...prev, last_name: e.target.value }))}
                                                    className="w-full bg-surface border border-gray-300 dark:border-border rounded-lg px-3 py-2 text-sm text-slate-900 dark:text-content outline-none"
                                                />
                                            </div>
                                        </>
                                    ) : (
                                        <>
                                            <div className="space-y-1">
                                                <label className="text-[10px] font-bold text-slate-600 dark:text-content-muted uppercase tracking-widest">Entreprise</label>
                                                <input
                                                    type="text"
                                                    value={addForm.company_name}
                                                    onChange={(e) => setAddForm((prev) => ({ ...prev, company_name: e.target.value }))}
                                                    className="w-full bg-surface border border-gray-300 dark:border-border rounded-lg px-3 py-2 text-sm text-slate-900 dark:text-content outline-none"
                                                />
                                            </div>
                                            <div className="space-y-1">
                                                <label className="text-[10px] font-bold text-slate-600 dark:text-content-muted uppercase tracking-widest">Organisateur</label>
                                                <input
                                                    type="text"
                                                    value={addForm.organizer_name}
                                                    onChange={(e) => setAddForm((prev) => ({ ...prev, organizer_name: e.target.value }))}
                                                    className="w-full bg-surface border border-gray-300 dark:border-border rounded-lg px-3 py-2 text-sm text-slate-900 dark:text-content outline-none"
                                                />
                                            </div>
                                        </>
                                    )}

                                    <div className="space-y-1">
                                        <label className="text-[10px] font-bold text-slate-600 dark:text-content-muted uppercase tracking-widest">Téléphone</label>
                                        <input
                                            type="text"
                                            value={addForm.phone}
                                            onChange={(e) => setAddForm((prev) => ({ ...prev, phone: e.target.value }))}
                                            className="w-full bg-surface border border-gray-300 dark:border-border rounded-lg px-3 py-2 text-sm text-slate-900 dark:text-content outline-none"
                                        />
                                    </div>
                                    <div className="space-y-1">
                                        <label className="text-[10px] font-bold text-slate-600 dark:text-content-muted uppercase tracking-widest">Email</label>
                                        <input
                                            type="email"
                                            value={addForm.email}
                                            onChange={(e) => setAddForm((prev) => ({ ...prev, email: e.target.value }))}
                                            className="w-full bg-surface border border-gray-300 dark:border-border rounded-lg px-3 py-2 text-sm text-slate-900 dark:text-content outline-none"
                                        />
                                    </div>

                                    <div className="md:col-span-2 space-y-1">
                                        <label className="text-[10px] font-bold text-slate-600 dark:text-content-muted uppercase tracking-widest">Adresse postale</label>
                                        <input
                                            type="text"
                                            value={addForm.address}
                                            onChange={(e) => setAddForm((prev) => ({ ...prev, address: e.target.value }))}
                                            className="w-full bg-surface border border-gray-300 dark:border-border rounded-lg px-3 py-2 text-sm text-slate-900 dark:text-content outline-none"
                                        />
                                    </div>

                                    <div className="space-y-1">
                                        <label className="text-[10px] font-bold text-slate-600 dark:text-content-muted uppercase tracking-widest">Code postal</label>
                                        <input
                                            type="text"
                                            value={addForm.postal_code}
                                            onChange={(e) => setAddForm((prev) => ({ ...prev, postal_code: e.target.value }))}
                                            className="w-full bg-surface border border-gray-300 dark:border-border rounded-lg px-3 py-2 text-sm text-slate-900 dark:text-content outline-none"
                                        />
                                    </div>

                                    <div className="space-y-1">
                                        <label className="text-[10px] font-bold text-slate-600 dark:text-content-muted uppercase tracking-widest">Ville</label>
                                        <input
                                            type="text"
                                            value={addForm.city}
                                            onChange={(e) => setAddForm((prev) => ({ ...prev, city: e.target.value }))}
                                            className="w-full bg-surface border border-gray-300 dark:border-border rounded-lg px-3 py-2 text-sm text-slate-900 dark:text-content outline-none"
                                        />
                                    </div>

                                    <div className="md:col-span-2 space-y-1">
                                        <label className="text-[10px] font-bold text-slate-600 dark:text-content-muted uppercase tracking-widest">Pays</label>
                                        <input
                                            type="text"
                                            value={addForm.country}
                                            onChange={(e) => setAddForm((prev) => ({ ...prev, country: e.target.value }))}
                                            className="w-full bg-surface border border-gray-300 dark:border-border rounded-lg px-3 py-2 text-sm text-slate-900 dark:text-content outline-none"
                                        />
                                    </div>
                                </div>

                                {addError && (
                                    <p className="text-xs text-red-400 font-semibold">{addError}</p>
                                )}

                                <div className="flex gap-3 pt-2">
                                    <button
                                        type="button"
                                        onClick={() => setShowAddModal(false)}
                                        disabled={creatingContact}
                                        className="flex-1 px-4 py-3 rounded-xl border border-border text-slate-700 dark:text-content-muted font-bold hover:bg-gray-50 dark:hover:bg-surface-soft transition-colors disabled:opacity-60"
                                    >
                                        Annuler
                                    </button>
                                    <button
                                        type="button"
                                        onClick={handleCreateContact}
                                        disabled={creatingContact}
                                        className="flex-1 px-4 py-3 rounded-xl bg-brand-600 hover:bg-brand-700 text-brand-fg font-bold hover:opacity-90 disabled:opacity-50 transition-colors"
                                    >
                                        {creatingContact ? 'Création...' : 'Ajouter le contact'}
                                    </button>
                                </div>
                            </div>
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>
        </div>
    );
};
